import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Slider,
  SliderFilledTrack,
  SliderThumb,
  SliderTrack,
  Spinner,
  Stack,
  Text,
  Textarea,
  useToast,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AssessmentTemplateDto, bffApi, UpdateDimensionScoreDto } from "../../../../autogen/bff-api";

interface OrganizationAssessmentFormProps {
  organizationId?: string;
  organizationEntryId?: string;
  organizationName: string;
  template: AssessmentTemplateDto;
  onAssessmentComplete?: () => void;
}

export const OrganizationAssessmentForm = ({
  organizationId,
  organizationEntryId,
  organizationName,
  template,
  onAssessmentComplete,
}: OrganizationAssessmentFormProps) => {
  const { t } = useTranslation();
  const toast = useToast();
  const [createAssessment, { isLoading: isCreating }] = bffApi.useCreateAssessmentMutation();

  const isLoading = isCreating;

  const [formData, setFormData] = useState<{
    name: string;
    description: string;
    dimensions: UpdateDimensionScoreDto[];
  }>({
    name: `${organizationName} Assessment`,
    description: "",
    dimensions: [],
  });

  useEffect(() => {
    const templateDimensions = template.dimensions.map((dim) => ({
      dimensionId: dim.id,
      directScore: 3,
      comment: "",
    }));

    setFormData((prev) => ({
      ...prev,
      dimensions: templateDimensions,
    }));
  }, [template]);

  const handleDimensionScoreChange = (dimensionId: string, value: number) => {
    setFormData((prev) => ({
      ...prev,
      dimensions: prev.dimensions.map((dim) =>
        dim.dimensionId === dimensionId ? { ...dim, directScore: value } : dim
      ),
    }));
  };

  const handleDimensionDescriptionChange = (dimensionId: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      dimensions: prev.dimensions.map((dim) => (dim.dimensionId === dimensionId ? { ...dim, comment: value } : dim)),
    }));
  };

  const handleSubmit = async () => {
    try {
      const assessedEntity = organizationEntryId
        ? { organizationEntryId: organizationEntryId }
        : { organizationId: organizationId };

      await createAssessment({
        createAssessmentRequestDto: {
          name: formData.name,
          description: formData.description,
          assessedEntity,
          templateId: template.id,
          dimensionScores: formData.dimensions.map((dim) => ({
            dimensionId: dim.dimensionId,
            directScore: dim.directScore,
            comment: dim.comment,
          })),
        },
      }).unwrap();

      toast({
        title: t("Assessment Submitted"),
        description: t("The organization assessment has been successfully submitted."),
        status: "success",
        duration: 5000,
        isClosable: true,
      });

      if (onAssessmentComplete) {
        onAssessmentComplete();
      }
    } catch (error) {
      console.error("Error creating or updating assessment:", error);
      toast({
        title: t("Assessment Failed"),
        description: `${t("Failed to submit assessment")}. ${t("Please try again")}.`,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  if (isLoading) {
    return (
      <Box p={4} borderWidth="1px" borderRadius="lg" backgroundColor="smBackground" width="full">
        <Stack spacing={6} align="center" justify="center" minHeight="200px">
          <Spinner size="xl" />
          <Text>{t("Submitting assessment")}...</Text>
        </Stack>
      </Box>
    );
  }

  return (
    <Box p={4} width="full">
      <form
        id="assessment-form"
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
      >
        <Stack spacing={6}>
          <Box>
            <Heading size="md">{t("Assess Organization")}</Heading>
            <Text color="smMuted" fontSize="md" mt={1}>
              {t("Using template")}:{" "}
              <Text as="span" fontWeight="medium">
                {template.name}
              </Text>
            </Text>
          </Box>

          <FormControl isRequired>
            <FormLabel>{t("Assessment Name")}</FormLabel>
            <Input value={formData.name} onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))} />
          </FormControl>

          <FormControl>
            <FormLabel>{t("Description")}</FormLabel>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
              placeholder={`${t("Add notes about this assessment")}...`}
            />
          </FormControl>

          <Box>
            <Text fontWeight="semibold" mb={4}>
              {t("Assessment Dimensions")}
            </Text>
            <Stack spacing={4}>
              {formData.dimensions.map((dimension) => {
                return (
                  <FormControl key={dimension.dimensionId}>
                    <FormLabel>
                      {template.dimensions.find((d) => d.id === dimension.dimensionId)?.name || dimension.dimensionId}
                    </FormLabel>
                    <Stack direction="row" alignItems="center" spacing={4}>
                      <Slider
                        value={dimension.directScore || 0}
                        min={1}
                        max={5}
                        step={1}
                        onChange={(val) => handleDimensionScoreChange(dimension.dimensionId, val)}
                        mb={3}
                        width="full"
                      >
                        <SliderTrack>
                          <SliderFilledTrack />
                        </SliderTrack>
                        <SliderThumb />
                      </Slider>
                      <Text fontWeight="bold" minWidth="40px" textAlign="center">
                        {(dimension.directScore || 0).toFixed(1)}
                      </Text>
                    </Stack>
                    <Textarea
                      placeholder={`${t("Add context (optional)")}...`}
                      value={dimension.comment || ""}
                      onChange={(e) => handleDimensionDescriptionChange(dimension.dimensionId, e.target.value)}
                      mb={4}
                    />
                  </FormControl>
                );
              })}
            </Stack>
          </Box>

          {/* Hidden submit button for form validation */}
          <Button type="submit" hidden isLoading={isLoading}>
            {t("Submit Assessment")}
          </Button>
        </Stack>
      </form>
    </Box>
  );
};
